import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle, Star, TruckIcon, Shield, Clock, Sparkles, Zap, Award, Phone, Mail, MapPin, Play, Building2, Users, Target, Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ProductCard } from '../components/ProductCard';
import { AnimatedDucts } from '../components/AnimatedDucts';
import { useWishlist } from '../contexts/WishlistContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';

// CSS Animations as inline styles - Professional & Clean
const heroStyles = `
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-12px); }
  }
  @keyframes float-reverse {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(12px); }
  }
  @keyframes spin-slow {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  @keyframes fade-in-up {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-float { animation: float 5s ease-in-out infinite; }
  .animate-float-reverse { animation: float-reverse 6s ease-in-out infinite; }
  .animate-spin-slow { animation: spin-slow 25s linear infinite; }
  .animate-fade-in-up { animation: fade-in-up 0.6s ease-out forwards; }
`;

export const Home = () => {
  const navigate = useNavigate();
  const { wishlistIds, isInWishlist, toggleWishlist } = useWishlist();
  const [featuredProducts, setFeaturedProducts] = useState([]);

  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?featured=true`);
      const list = Array.isArray(response.data) ? response.data : [];
      setFeaturedProducts(list.slice(0, 4));
    } catch (error) {
      console.error('Error fetching featured products:', error);
      setFeaturedProducts([]);
    }
  };


  return (
    <div className="min-h-screen overflow-hidden">
      <style>{heroStyles}</style>
      
      {/* Hero Section - Modern Blue Style */}
      <section className="relative min-h-[100vh] flex items-center overflow-hidden pt-20">
        {/* Gradient Background - Very Dark Blue */}
        <div className="absolute inset-0 bg-[#0f172a]"></div>
        
        {/* Subtle Professional Background Effects */}
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-blue-500/8 rounded-full blur-[100px]"></div>
        
        {/* Subtle Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}></div>
        
        {/* Bottom Gradient Fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-slate-900/50 to-transparent"></div>
        
        {/* Main Content */}
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center py-16">
            {/* Left Content */}
            <div data-testid="hero-content" className="text-center lg:text-left">
              {/* Badge */}
              <div className="inline-flex items-center px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-400 text-sm font-medium mb-6 animate-fade-in-up">
                <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                Líder em Soluções Industriais
              </div>
              
              {/* Main Heading */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                Excelência em{' '}
                <span className="text-blue-400">Dutos</span>{' '}
                e <span className="text-blue-400">Exaustores</span> Industriais
              </h1>
              
              {/* Description */}
              <p className="text-lg text-slate-400 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                Soluções completas em ventilação industrial com mais de 15 anos de experiência. 
                Qualidade certificada e atendimento em todo o Brasil.
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                <Button 
                  size="lg" 
                  onClick={() => navigate('/shop')} 
                  data-testid="shop-now-btn"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-base font-medium rounded-lg shadow-lg shadow-blue-900/30 transition-all duration-300 hover:shadow-blue-900/50"
                >
                  Ver Produtos
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}
                  className="border border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white px-8 py-6 text-base font-medium rounded-lg transition-all duration-300"
                >
                  Conheça a Empresa
                </Button>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-8 mt-12 pt-8 border-t border-slate-700/50 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                {[
                  { value: '15+', label: 'Anos no Mercado' },
                  { value: '5.000+', label: 'Projetos Entregues' },
                  { value: '100%', label: 'Satisfação' },
                ].map((stat, index) => (
                  <div key={index} className="text-center lg:text-left">
                    <p className="text-2xl md:text-3xl font-bold text-white mb-1">
                      {stat.value}
                    </p>
                    <p className="text-slate-500 text-sm">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Right Content - Animated SVG */}
            <div className="relative hidden lg:flex items-center justify-center">
              {/* Decorative circle */}
              <div className="absolute w-[450px] h-[450px] border border-slate-700/50 rounded-full"></div>
              
              {/* Animated Ducts SVG */}
              <div className="relative w-[480px] h-[300px]">
                <div className="relative overflow-hidden border border-slate-700 shadow-2xl shadow-blue-900/20 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl">
                  <AnimatedDucts className="w-full h-full" />
                </div>
              </div>
              
              {/* Floating Cards - Professional Style */}
              <div className="absolute -bottom-6 -left-4 bg-slate-800/90 backdrop-blur-sm rounded-xl p-4 shadow-xl border border-slate-700 animate-float" style={{ animationDelay: '0.5s' }}>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">ISO 9001</p>
                    <p className="text-slate-400 text-xs">Certificada</p>
                  </div>
                </div>
              </div>
              
              <div className="absolute -top-2 -right-4 bg-slate-800/90 backdrop-blur-sm rounded-xl p-4 shadow-xl border border-slate-700 animate-float-reverse">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <TruckIcon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">Entrega</p>
                    <p className="text-slate-400 text-xs">Todo Brasil</p>
                  </div>
                </div>
              </div>
              
              <div className="absolute top-1/2 -right-12 bg-slate-800/90 backdrop-blur-sm rounded-xl p-4 shadow-xl border border-slate-700 animate-float" style={{ animationDelay: '1s' }}>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <Award className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-white text-sm">15+ Anos</p>
                    <p className="text-slate-400 text-xs">Experiência</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator - Minimal */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <div className="w-6 h-10 border border-slate-600 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-blue-500 rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Features Section - Clean Professional Style */}
      <section className="py-20 bg-white dark:bg-slate-900 relative">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <p className="text-blue-600 dark:text-blue-400 text-sm font-semibold uppercase tracking-wider mb-3">
              Diferenciais
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">
              Por que escolher a PCS Dutos?
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: 'Qualidade Certificada', desc: 'Produtos com certificação ISO e garantia total' },
              { icon: TruckIcon, title: 'Entrega Regional', desc: 'Logística eficiente para todo o Rio de Janeiro' },
              { icon: Clock, title: 'Suporte Técnico', desc: 'Equipe especializada sempre disponível' },
              { icon: Zap, title: 'Alta Durabilidade', desc: 'Produtos de longa vida útil e eficiência' },
            ].map((feature, i) => (
              <div 
                key={i} 
                className="group bg-slate-50 dark:bg-slate-800 rounded-xl p-6 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-300 border border-slate-200 dark:border-slate-700 text-center md:text-left"
                data-testid={i === 0 ? "feature-quality" : undefined}
              >
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4 mx-auto md:mx-0">
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-semibold text-lg mb-2 text-slate-900 dark:text-white">{feature.title}</h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-slate-50 dark:bg-slate-800">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-blue-600 dark:text-blue-400 text-sm font-semibold uppercase tracking-wider mb-3">
                Serviços
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
                Soluções Completas para sua Indústria
              </h2>
              <p className="text-slate-600 dark:text-slate-400 mb-8">
                Atendemos todas as necessidades do seu projeto industrial com excelência.
              </p>
              <div className="space-y-3">
                {[
                  { title: 'Projetos Personalizados', desc: 'Soluções sob medida para sua empresa' },
                  { title: 'Instalação Profissional', desc: 'Equipe técnica especializada' },
                  { title: 'Manutenção Preventiva', desc: 'Programas para maior durabilidade' },
                  { title: 'Consultoria Técnica', desc: 'Orientação especializada' },
                ].map((service, i) => (
                  <div key={i} className="flex items-start space-x-3 p-4 bg-white dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
                    <div className="w-8 h-8 bg-blue-600 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900 dark:text-white text-sm">{service.title}</h4>
                      <p className="text-slate-500 dark:text-slate-400 text-sm">{service.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <img 
                    src="https://images.unsplash.com/photo-1590959651373-a3db0f38a961?w=400&h=300&fit=crop" 
                    alt="Dutos de ventilação industrial" 
                    className="rounded-xl shadow-lg"
                  />
                  <img 
                    src="https://images.unsplash.com/photo-1581094651181-35942459ef62?w=400&h=400&fit=crop" 
                    alt="Sistema de exaustão industrial" 
                    className="rounded-xl shadow-lg"
                  />
                </div>
                <div className="space-y-4 pt-8">
                  <img 
                    src="https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400&h=400&fit=crop" 
                    alt="Instalação de dutos metálicos" 
                    className="rounded-xl shadow-lg"
                  />
                  <img 
                    src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=400&h=300&fit=crop" 
                    alt="Instalação de sistemas de ventilação" 
                    className="rounded-xl shadow-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products - Premium Carousel Style */}
      <section className="py-24 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 overflow-hidden relative">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-600/5 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <p className="text-blue-400 text-sm font-semibold uppercase tracking-widest mb-4">
              Produtos
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Produtos em Destaque
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto text-lg">
              Nossa seleção de produtos mais vendidos
            </p>
          </div>
          
          {/* Cards Container - 3 Products Premium Style */}
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto"
            data-testid="featured-products"
          >
            {(Array.isArray(featuredProducts) ? featuredProducts : []).slice(0, 3).map((product, index) => {
              const isCenter = index === 1;
              return (
                <div 
                  key={product.id}
                  onClick={() => navigate(`/product/${product.id}`)}
                  className={`relative group cursor-pointer transition-all duration-500
                    ${isCenter 
                      ? 'lg:scale-105 lg:-translate-y-6 lg:z-20' 
                      : 'lg:z-10 lg:hover:scale-105'
                    }`}
                >
                  {/* Card Container */}
                  <div className={`relative rounded-3xl overflow-hidden bg-gradient-to-b from-slate-800/80 to-slate-900/90 backdrop-blur-sm border transition-all duration-500
                    ${isCenter 
                      ? 'border-blue-500/30 shadow-2xl shadow-blue-500/20 hover:shadow-blue-500/30' 
                      : 'border-slate-700/50 hover:border-slate-600/50'
                    }`}
                  >
                    {/* Product Image */}
                    <div className="relative overflow-hidden h-[240px]">
                      <img
                        src={product.images?.[0] || 'https://via.placeholder.com/400'}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/20 to-transparent"></div>
                      
                      {/* Badge */}
                      {product.featured && (
                        <div className={`absolute top-4 left-4 text-white text-xs font-semibold px-4 py-1.5 rounded-full backdrop-blur-sm
                          ${isCenter ? 'bg-blue-600' : 'bg-slate-700/80'}`}
                        >
                          Destaque
                        </div>
                      )}
                      
                      {/* Wishlist Button */}
                      <button 
                        onClick={async (e) => {
                          e.stopPropagation();
                          const result = await toggleWishlist(product.id);
                          if (!result?.success && result?.error === 'not_logged_in') {
                            navigate('/auth');
                          }
                        }}
                        className="absolute top-4 right-4 w-10 h-10 bg-slate-900/60 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-slate-800 border border-slate-700/50"
                      >
                        <svg 
                          className={`w-5 h-5 transition-colors ${
                            Array.isArray(wishlistIds) && isInWishlist(product.id)
                              ? 'text-red-500 fill-red-500' 
                              : 'text-white'
                          }`} 
                          fill={Array.isArray(wishlistIds) && isInWishlist(product.id) ? 'currentColor' : 'none'}
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                      </button>
                    </div>
                    
                    {/* Product Info */}
                    <div className="p-5 pb-6">
                      <h3 className="font-semibold text-white mb-2 line-clamp-2 text-lg">
                        {product.name}
                      </h3>
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-blue-400 text-xl">
                          R$ {product.price?.toFixed(2).replace('.', ',')}
                        </span>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/product/${product.id}`);
                          }}
                          className="px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          Ver mais
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Glow effect for center card */}
                  {isCenter && (
                    <div className="absolute -inset-1 bg-gradient-to-r from-blue-600/20 via-blue-500/10 to-blue-600/20 rounded-3xl blur-xl -z-10 opacity-60 group-hover:opacity-80 transition-opacity"></div>
                  )}
                </div>
              );
            })}
          </div>
          
          <div className="text-center">
            <Button 
              size="lg" 
              onClick={() => navigate('/shop')} 
              data-testid="view-all-products-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-6 text-base font-medium rounded-full transition-all duration-300 shadow-lg shadow-blue-600/25 hover:shadow-blue-600/40"
            >
              Ver Todos os Produtos
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=800&h=600&fit=crop"
                alt="About us"
                className="rounded-xl shadow-xl"
              />
              {/* Experience badge */}
              <div className="absolute -bottom-6 -right-6 bg-blue-600 text-white rounded-xl p-5 shadow-lg">
                <p className="text-3xl font-bold">15+</p>
                <p className="text-sm text-blue-100">Anos de Experiência</p>
              </div>
            </div>
            <div>
              <p className="text-blue-400 text-sm font-semibold uppercase tracking-wider mb-3">
                Sobre Nós
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Excelência em Soluções Industriais
              </h2>
              <p className="text-slate-400 mb-4 leading-relaxed">
                Há mais de 15 anos no mercado, somos especialistas em dutos e exaustores industriais. Nossa missão é fornecer produtos de alta qualidade que atendam às necessidades mais exigentes do setor.
              </p>
              <p className="text-slate-400 mb-6 leading-relaxed">
                Trabalhamos com os melhores materiais e tecnologias para garantir durabilidade e eficiência.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: Award, text: 'Produtos certificados' },
                  { icon: Shield, text: 'Garantia de qualidade' },
                  { icon: TruckIcon, text: 'Entrega regional' },
                  { icon: Clock, text: 'Suporte técnico' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center space-x-2 p-3 bg-slate-800 border border-slate-700 rounded-lg">
                    <div className="w-8 h-8 bg-blue-600 rounded-md flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-slate-300 text-sm font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-slate-50 dark:bg-slate-800">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-blue-600 dark:text-blue-400 text-sm font-semibold uppercase tracking-wider mb-3">
              Depoimentos
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-3">
              O Que Dizem Nossos Clientes
            </h2>
            <p className="text-slate-600 dark:text-slate-400 max-w-xl mx-auto">
              Confiança construída ao longo de 15 anos de parceria
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: 'Carlos Silva', role: 'Gerente Industrial', text: 'Excelente qualidade! Os dutos superaram todas as expectativas. Equipe muito profissional.' },
              { name: 'Ana Santos', role: 'Engenheira de Produção', text: 'Entrega rápida e produtos de primeira linha. Recomendo para qualquer projeto industrial.' },
              { name: 'Roberto Lima', role: 'Diretor de Operações', text: 'Parceria de anos com resultados sempre positivos. Qualidade e compromisso incomparáveis.' },
            ].map((testimonial, i) => (
              <div key={i} className="bg-white dark:bg-slate-700 p-6 rounded-xl border border-slate-200 dark:border-slate-600 shadow-sm">
                <div className="flex space-x-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-700 dark:text-slate-300 mb-4 text-sm leading-relaxed">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                    {testimonial.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white text-sm">{testimonial.name}</p>
                    <p className="text-slate-500 dark:text-slate-400 text-xs">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-blue-400 text-sm font-semibold uppercase tracking-wider mb-3">
                Contato
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                Entre em Contato
              </h2>
              <p className="text-slate-400 max-w-xl mx-auto">
                Nossa equipe está pronta para ajudar você a encontrar a melhor solução
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4 mb-10">
              <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 text-center">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Phone className="h-5 w-5 text-white" />
                </div>
                <p className="text-white font-medium text-sm mb-1">Telefone</p>
                <p className="text-slate-400 text-sm">(21) 97195-6796</p>
              </div>
              <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 text-center">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Mail className="h-5 w-5 text-white" />
                </div>
                <p className="text-white font-medium text-sm mb-1">Email</p>
                <p className="text-slate-400 text-sm">pcsdutoseexaustores@gmail.com</p>
              </div>
              <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 text-center">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <MapPin className="h-5 w-5 text-white" />
                </div>
                <p className="text-white font-medium text-sm mb-1">Endereço</p>
                <p className="text-slate-400 text-sm">Estrada da Palhada 3392 - Nova Iguaçu - RJ</p>
              </div>
            </div>
            
            <div className="text-center">
              <Button 
                size="lg"
                onClick={() => {
                  const whatsappNumber = '5521971956796';
                  const message =
                    'Olá! Vim pelo site da PCS Dutos e Exaustores.\n\n' +
                    'Quero solicitar um orçamento.\n' +
                    '- Produto (opcional): \n' +
                    '- Quantidade (opcional): \n\n' +
                    'Meu nome: ';
                  const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
                  window.open(url, '_blank');
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-base font-medium rounded-lg transition-colors duration-300"
              >
                Solicitar Orçamento
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};